import type {
  CommentSocketClientMessage,
  CommentSocketCountItem,
  CommentSocketErrorPayload,
  CommentSocketServerMessage,
  CommentSocketTarget,
} from '@carvia/ros-client-types';
import { API_URL } from '@/constants/api-url.constant';
import type { CommentableEntityType, CommentThread } from '@/api/comments.service';

type CountHandler = (count: number) => void;
type ListHandler = (comments: CommentThread[]) => void;
type ErrorHandler = (error: Error) => void;

const BATCH_MS = 50;
const MAX_BATCH = 500;
const POLICY_VIOLATION = 1008;

export class CommentSocketError extends Error {
  readonly terminal: boolean;

  constructor(message: string, terminal = false) {
    super(message);
    this.name = 'CommentSocketError';
    this.terminal = terminal;
  }
}

const keyOf = ({ entityType, entityId }: CommentSocketTarget): string =>
  `${entityType}:${entityId}`;

const wsUrl = (): string =>
  `${API_URL.value.replace(/\/$/, '').replace(/^http/, 'ws')}/v1/comments`;

let socket: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let attempts = 0;

const counts = new Map<string, Set<CountHandler>>();
const lists = new Map<string, Set<ListHandler>>();
const countErrors = new Map<string, Set<ErrorHandler>>();
const listErrors = new Map<string, Set<ErrorHandler>>();
const pendingCountSubs = new Map<string, CommentSocketTarget>();
const pendingCountUnsubs = new Map<string, CommentSocketTarget>();

const addHandler = <T>(map: Map<string, Set<T>>, key: string, handler: T): void => {
  let handlers = map.get(key);
  if (!handlers) {
    handlers = new Set();
    map.set(key, handlers);
  }
  handlers.add(handler);
};

const removeHandler = <T>(map: Map<string, Set<T>>, key: string, handler: T): void => {
  const handlers = map.get(key);
  handlers?.delete(handler);
  if (handlers && handlers.size === 0) {
    map.delete(key);
  }
};

const emit = (map: Map<string, Set<ErrorHandler>>, key: string, error: Error): void => {
  [...(map.get(key) ?? [])].forEach((handler) => {
    handler(error);
  });
};

const emitAll = (error: Error): void => {
  for (const key of [...countErrors.keys()]) {
    emit(countErrors, key, error);
  }
  for (const key of [...listErrors.keys()]) {
    emit(listErrors, key, error);
  }
};

const failWatchers = (error: Error, target?: CommentSocketTarget | null): void => {
  if (target) {
    emit(countErrors, keyOf(target), error);
    emit(listErrors, keyOf(target), error);
    return;
  }
  emitAll(error);
};

const targetFromError = (data: CommentSocketErrorPayload): CommentSocketTarget | null => {
  if (data.entityId && data.entityType) {
    return { entityId: data.entityId, entityType: data.entityType };
  }
  return null;
};

const send = (message: CommentSocketClientMessage): void => {
  if (socket?.readyState !== WebSocket.OPEN) {
    return;
  }
  socket.send(JSON.stringify(message));
};

const sendCountBatch = (
  event: 'subscribe' | 'unsubscribe',
  targets: CommentSocketTarget[],
): void => {
  for (let i = 0; i < targets.length; i += MAX_BATCH) {
    send({ event, data: { kind: 'count', items: targets.slice(i, i + MAX_BATCH) } });
  }
};

const flushCountBatches = (): void => {
  flushTimer = null;
  const subs = [...pendingCountSubs.values()];
  const unsubs = [...pendingCountUnsubs.values()];
  pendingCountSubs.clear();
  pendingCountUnsubs.clear();
  sendCountBatch('subscribe', subs);
  sendCountBatch('unsubscribe', unsubs);
};

const scheduleCountFlush = (): void => {
  flushTimer ??= setTimeout(flushCountBatches, BATCH_MS);
};

const queueCountWatch = (
  action: 'subscribe' | 'unsubscribe',
  target: CommentSocketTarget,
): void => {
  const key = keyOf(target);
  if (action === 'subscribe') {
    pendingCountUnsubs.delete(key);
    pendingCountSubs.set(key, target);
  } else {
    pendingCountSubs.delete(key);
    pendingCountUnsubs.set(key, target);
  }
  if (socket?.readyState === WebSocket.OPEN) {
    scheduleCountFlush();
  }
};

const replay = (): void => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  pendingCountSubs.clear();
  pendingCountUnsubs.clear();
  sendCountBatch(
    'subscribe',
    [...counts.keys()].map((key) => {
      const [entityType, entityId] = key.split(':') as [CommentSocketTarget['entityType'], string];
      return { entityType, entityId };
    }),
  );
  for (const key of lists.keys()) {
    const [entityType, entityId] = key.split(':') as [CommentSocketTarget['entityType'], string];
    send({ event: 'subscribe', data: { kind: 'list', entityType, entityId } });
  }
};

const handleCounts = (items: CommentSocketCountItem[]): void => {
  for (const item of items) {
    counts.get(keyOf(item))?.forEach((handler) => {
      handler(item.count);
    });
  }
};

const parseServerMessage = (raw: string): CommentSocketServerMessage | null => {
  try {
    const message = JSON.parse(raw) as CommentSocketServerMessage;
    if (message.event === 'counts' || message.event === 'list' || message.event === 'error') {
      return message;
    }
    return null;
  } catch {
    return null;
  }
};

const handleMessage = (raw: string): void => {
  const message = parseServerMessage(raw);
  if (!message) {
    return;
  }
  if (message.event === 'counts') {
    handleCounts(message.data.items);
    return;
  }
  if (message.event === 'list') {
    lists.get(keyOf(message.data))?.forEach((handler) => {
      handler(message.data.comments as unknown as CommentThread[]);
    });
    return;
  }
  failWatchers(new CommentSocketError(message.data.message, true), targetFromError(message.data));
};

const disconnectIfIdle = (): void => {
  if (counts.size || lists.size) {
    return;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  pendingCountSubs.clear();
  pendingCountUnsubs.clear();
  socket?.close();
  socket = null;
};

const connect = (): void => {
  if (socket || (!counts.size && !lists.size)) {
    return;
  }
  const next = new WebSocket(wsUrl());
  socket = next;
  next.addEventListener('open', () => {
    if (socket !== next) {
      return;
    }
    attempts = 0;
    replay();
  });
  next.addEventListener('message', (event) => {
    if (typeof event.data === 'string') {
      handleMessage(event.data);
    }
  });
  next.addEventListener('close', (event: CloseEvent) => {
    if (socket !== next) {
      return;
    }
    socket = null;
    const terminal = event.code === POLICY_VIOLATION;
    failWatchers(new CommentSocketError(event.reason || 'Disconnected', terminal));
    if (!counts.size && !lists.size) {
      return;
    }
    if (terminal) {
      return;
    }
    const delay = Math.min(1000 * 2 ** attempts, 15_000);
    attempts += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  });
};

const watchCount = (
  target: CommentSocketTarget,
  handler: CountHandler,
  onError?: ErrorHandler,
): (() => void) => {
  const key = keyOf(target);
  let handlers = counts.get(key);
  if (!handlers) {
    handlers = new Set();
    counts.set(key, handlers);
    queueCountWatch('subscribe', target);
    connect();
  }
  handlers.add(handler);
  if (onError) {
    addHandler(countErrors, key, onError);
  }
  return () => {
    handlers?.delete(handler);
    if (onError) {
      removeHandler(countErrors, key, onError);
    }
    if (handlers && handlers.size === 0) {
      counts.delete(key);
      queueCountWatch('unsubscribe', target);
      disconnectIfIdle();
    }
  };
};

const watchList = (
  target: CommentSocketTarget,
  handler: ListHandler,
  onError?: ErrorHandler,
): (() => void) => {
  const key = keyOf(target);
  let handlers = lists.get(key);
  if (!handlers) {
    handlers = new Set();
    lists.set(key, handlers);
    send({ event: 'subscribe', data: { kind: 'list', ...target } });
    connect();
  }
  handlers.add(handler);
  if (onError) {
    addHandler(listErrors, key, onError);
  }
  return () => {
    handlers?.delete(handler);
    if (onError) {
      removeHandler(listErrors, key, onError);
    }
    if (handlers && handlers.size === 0) {
      lists.delete(key);
      send({ event: 'unsubscribe', data: { kind: 'list', ...target } });
      disconnectIfIdle();
    }
  };
};

export const subscribeCommentCount = (
  entityType: CommentableEntityType,
  entityId: string,
  handler: CountHandler,
  onError?: ErrorHandler,
): (() => void) =>
  watchCount(
    { entityType: entityType as CommentSocketTarget['entityType'], entityId },
    handler,
    onError,
  );

export const subscribeCommentList = (
  entityType: CommentableEntityType,
  entityId: string,
  handler: ListHandler,
  onError?: ErrorHandler,
): (() => void) =>
  watchList(
    { entityType: entityType as CommentSocketTarget['entityType'], entityId },
    handler,
    onError,
  );
